import { DragEvent } from "react";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDevice } from "@/hooks/useDevice";
import { useTranslation, Trans } from "react-i18next";

interface DropZoneProps {
  onFileSelect: (file: File) => void;
  isDragging: boolean;
  onDragOver: (e: DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent<HTMLDivElement>) => void;
  triggerFileInput: () => void;
}

const DropZone = ({ onFileSelect, isDragging, onDragOver, onDragLeave, onDrop, triggerFileInput }: DropZoneProps) => {
  const { isMobile, shouldDisableDragDrop } = useDevice();

  const { t } = useTranslation();

  // Адаптивные тексты
  const title = t('index.dropzone.title');
  const description = isMobile
    ? t('index.dropzone.desc_mobile')
    : t('index.dropzone.desc_desktop');

  const buttonText = isMobile
    ? t('index.dropzone.btn_mobile')
    : t('index.dropzone.btn_desktop');

  return (
    <div
      className={`
        relative overflow-hidden rounded-xl border-2 border-dashed transition-all duration-300 bg-gradient-to-br from-card to-muted/20 hover:from-brand-50/50 hover:to-brand-100/20 hover:border-brand-300 hover:shadow-lg group cursor-pointer
        ${isDragging && !shouldDisableDragDrop ? "border-brand-400 bg-gradient-to-br from-brand-50 to-brand-100/50 shadow-xl scale-[1.02]" : "border-border/50"} 
        ${isMobile ? "p-6 min-h-[200px]" : "p-8 min-h-[240px]"}
      `}
      onDragOver={!shouldDisableDragDrop ? onDragOver : undefined}
      onDragLeave={!shouldDisableDragDrop ? onDragLeave : undefined}
      onDrop={!shouldDisableDragDrop ? onDrop : undefined}
      onClick={triggerFileInput}
    >
      <div className="flex flex-col items-center justify-center h-full text-center">
        <div className="mb-4">
          <div className={`mx-auto bg-gradient-to-br from-brand-100 to-brand-200 text-brand-600 rounded-full flex items-center justify-center mb-3 shadow-sm group-hover:shadow-md group-hover:from-brand-200 group-hover:to-brand-300 transition-all duration-300 ${isMobile ? "h-12 w-12" : "h-16 w-16"
            } ${isDragging ? "scale-110 from-brand-200 to-brand-300" : ""}`}>
            <Upload size={isMobile ? 24 : 28} className="group-hover:scale-110 transition-transform duration-300" />
          </div>
          <h3 className={`font-semibold text-foreground ${isMobile ? "text-lg" : "text-xl"}`}>{title}</h3>
          <p className={`text-muted-foreground mt-2 leading-relaxed ${isMobile ? "text-sm" : "text-base"}`}>{description}</p>
        </div>

        <Button
          onClick={(e) => {
            e.stopPropagation();
            triggerFileInput();
          }}
          size="lg"
          className={`
            ${isMobile
              ? "w-full bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 text-white font-semibold py-4 px-6 text-base min-h-[48px] shadow-md hover:shadow-lg"
              : "bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 text-white font-semibold px-8 py-3 shadow-lg hover:shadow-xl hover:scale-105"
            } 
            transition-all duration-300 border-0
          `}
        >
          <Upload size={isMobile ? 20 : 20} className="mr-2" />
          {buttonText}
        </Button>

        <p className={`mt-4 text-muted-foreground leading-relaxed ${isMobile ? "text-xs" : "text-sm"}`}>
          <span className="inline-block"><Trans i18nKey="index.dropzone.max_size" components={{ strong: <strong /> }} /></span>
          <span className="mx-2 opacity-50">•</span>
          <span className="inline-block"><Trans i18nKey="index.dropzone.max_duration" components={{ strong: <strong /> }} /></span>
        </p>
      </div>
    </div>
  );
};

export default DropZone;
